import React, { useState } from 'react';
import { Button, Input, Modal, ModalBody } from 'reactstrap';
import { aiEditPrompt, applyPromptEdit } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import PromptDiff from './PromptDiff';
import '../../assets/css/--component-rubric.css';

/**
 * Ask for one change to a saved prompt, in plain words.
 *
 * One instruction at a time, because that is the only way anyone can tell
 * which change helped. The box is a single line's worth, not a paragraph, and
 * the backend returns only the sections the instruction touches.
 *
 * The button says "Show me the change", not "Apply". Nothing is written until
 * the diff has been read and something kept. A customer should never find out
 * their prompt changed because they pressed something that sounded like a
 * preview.
 */

/** The shape of a good instruction, shown rather than explained. */
const SUGGESTIONS = [
  'Be firmer about booking',
  'Shorter sentences',
  'Always take a phone number',
  'Never discuss pricing',
];

const AskAiToChange = ({ promptId, disabled, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState(null);

  const ask = async () => {
    const text = instruction.trim();
    if (!text) return;
    setAsking(true);
    try {
      const response = await aiEditPrompt(promptId, text);
      const data = response?.data?.data ?? response?.data;
      if (!response?.status || !Array.isArray(data?.changes)) {
        showToast('error', 'Could not work that out', data?.message || 'Nothing has changed. Please try again.');
        return;
      }
      if (!data.changes.length) {
        // A normal, useful answer. Inventing a change to look busy is how a
        // tool like this stops being believed.
        showToast('success', 'Nothing needs changing', data.note || 'It already says that.');
        setOpen(false);
        return;
      }
      setChanges(data.changes);
      setOpen(false);
    } catch {
      showToast('error', 'Could not work that out', 'Nothing has changed. Please try again.');
    } finally {
      setAsking(false);
    }
  };

  const keep = async (sections) => {
    setSaving(true);
    try {
      const response = await applyPromptEdit(
        promptId,
        sections.map((s) => ({ section: s.section, after: s.after })),
        instruction.trim(),
      );
      const data = response?.data?.data ?? response?.data;
      if (!response?.status) {
        showToast('error', 'Could not save that', data?.message || 'Please try again.');
        return;
      }
      showToast('success', 'Saved', 'The version it replaced is kept, so you can put it back.');
      setChanges(null);
      setInstruction('');
      if (onChanged) onChanged(data);
    } catch {
      showToast('error', 'Could not save that', 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button type="button" color="link" className="p-0 fs-14" disabled={disabled} onClick={() => setOpen(true)}>
        Ask AI to change this
      </Button>

      <Modal isOpen={open} toggle={() => setOpen(false)} centered size="lg">
        <ModalBody className="p-4">
          <h5 className="mb-1">What would you like it to do differently?</h5>
          <p className="fs-14 text-muted">
            One change at a time. You will see exactly what would move before anything is saved.
          </p>
          <Input
            type="textarea"
            rows="3"
            className="custom-textarea"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Stop offering discounts, and if someone asks about cost say a nurse will call back"
          />
          <p className="fs-13 text-muted mt-3 mb-1">Or pick one people often ask for</p>
          <div className="d-flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <Button key={s} size="sm" color="light" onClick={() => setInstruction(s)}>{s}</Button>
            ))}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="light" onClick={() => setOpen(false)}>Cancel</Button>
            <Button color="primary" disabled={asking || !instruction.trim()} onClick={ask}>
              {asking ? 'Working it out…' : 'Show me the change'}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PromptDiff
        open={!!changes}
        changes={changes || []}
        saving={saving}
        onCancel={() => setChanges(null)}
        onKeep={keep}
      />
    </>
  );
};

export default AskAiToChange;
